import {Box, CheckIcon, RadioBase, TypographyVariants} from '@gwc/main'
import {genericMemo} from '@yandex-delivery/utils'
import {useCallback, useId, useState} from 'react'

import {Typography} from '../typography'
import {classes} from './Radio.classes'
import {RadioProps, RadioSize} from './Radio.types'

const checkIcon = <CheckIcon />

const typographyVariants: Record<RadioSize, TypographyVariants> = {
  m: 'body2',
  s: 'caption1',
}

const Radio = genericMemo(function Radio<V extends string | undefined>({
  label,
  value = '',
  checked = false,
  name = '',
  size = 'm',
  error,
  onChange,
  ...props
}: RadioProps<V>) {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocusVisible, setIsFocusVisible] = useState(false)
  const hasError = typeof error === 'string'
  const id = useId()

  // TODO: переделать на react-aria, как в GWC
  const onMouseOver = useCallback(() => setIsHovered(true), [])
  const onMouseOut = useCallback(() => setIsHovered(false), [])
  const onFocus = useCallback((e: React.FocusEvent) => setIsFocusVisible(e.currentTarget.matches(':focus-visible')), [])
  const onBlur = useCallback(() => setIsFocusVisible(false), [])

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.currentTarget.value as V, e)
    },
    [onChange],
  )

  return (
    <>
      <Box
        alignItems="center"
        className={classes.root}
        flexWrap="nowrap"
        onBlur={onBlur}
        onFocusCapture={onFocus}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
      >
        <RadioBase
          checked={checked}
          checkIcon={checkIcon}
          error={hasError}
          focused={isFocusVisible}
          hovered={isHovered}
          labelId={id}
          name={name}
          onChange={changeHandler}
          size={size}
          value={value}
          {...props}
        />
        {!!label && (
          <Typography
            className={classes.label}
            Component="label"
            htmlFor={id}
            ml="m"
            variant={typographyVariants[size]}
          >
            {label}
          </Typography>
        )}
      </Box>
      {hasError && error && (
        <Typography color="error" variant="caption2">
          {error}
        </Typography>
      )}
    </>
  )
})

export default Radio
